"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { revenueSchema, RevenueFormValues } from "@/utils/helpers/validators/calculators/revenue-leakage.schema"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import Button from "@/components/ui/Button"
import FormRow from "./FormRow"
import { SliderRow } from "./SliderRow"
import { Metric, Bar, PromoCard, fmtCurrency } from "../SmallComponents"

type Result = {
   monthly: number
   annual: number
   rollup: number
   errLoss: number
   peakLoss: number
   upsellLoss: number
} | null

export default function RevenueCalculator() {
   const [result, setResult] = useState<Result>(null)

   const form = useForm<RevenueFormValues>({
      resolver: zodResolver(revenueSchema),
      defaultValues: {
         ordersPerDay: 350,
         avgTicket: 18,
         errorRate: 4,
         peakLossRate: 2,
         upsellRate: 3,
         upsellValue: 3,
         locations: 1,
      },
   })

   const errorRate = form.watch("errorRate")
   const peakLossRate = form.watch("peakLossRate")
   const upsellRate = form.watch("upsellRate")

   function onSubmit(data: RevenueFormValues) {
      const days = 30
      const locations = data.locations ?? 1

      const errLoss = data.ordersPerDay * (data.errorRate / 100) * data.avgTicket * days
      const peakLoss = data.ordersPerDay * (data.peakLossRate / 100) * data.avgTicket * days
      const upsellLoss = data.ordersPerDay * (data.upsellRate / 100) * data.upsellValue * days

      const monthly = errLoss + peakLoss + upsellLoss
      const annual = monthly * 12
      const rollup = monthly * locations

      setResult({ monthly, annual, rollup, errLoss, peakLoss, upsellLoss })
   }

   function reset() {
      form.reset()
      setResult(null)
   }

   const totalBreakdown = (result?.errLoss ?? 0) + (result?.peakLoss ?? 0) + (result?.upsellLoss ?? 0)
   const errWidth = totalBreakdown ? (result!.errLoss / totalBreakdown) * 100 : 0
   const peakWidth = totalBreakdown ? (result!.peakLoss / totalBreakdown) * 100 : 0
   const upsellWidth = totalBreakdown ? (result!.upsellLoss / totalBreakdown) * 100 : 0

   return (
      <div className="rounded-2xl border bg-white p-6 shadow-lg">
         <div className="space-y-1 p-3 text-center">
            <p className="text-2xl font-bold">POS Revenue Leakage Calculator</p>
            <p className="text-sm text-gray-600">
               Estimate monthly revenue leakage from order errors, peak-hour slowdown, and missed upsells.
            </p>
         </div>
         <div className="mx-auto grid max-w-6xl gap-6 px-3 py-12 md:grid-cols-[60%_40%]">
            {/* INPUT CARD */}
            <Card className="rounded-2xl shadow-lg">
               <CardHeader>
                  <CardTitle className="text-center text-lg font-semibold">Inputs (Single Location)</CardTitle>
                  <div className="h-[2px] w-full rounded-full bg-muted" />
               </CardHeader>
               <CardContent className="space-y-6">
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                     <FormRow
                        label="Average orders per day"
                        description="Typical daily order volume for one location."
                        error={form.formState.errors.ordersPerDay?.message}
                     >
                        <Input
                           className="mt-1 w-48 rounded-xl"
                           type="number"
                           {...form.register("ordersPerDay", { valueAsNumber: true })}
                        />
                     </FormRow>

                     <FormRow
                        label="Average ticket size ($)"
                        description="Average check amount per order."
                        error={form.formState.errors.avgTicket?.message}
                     >
                        <Input
                           className="mt-1 w-48 rounded-xl"
                           type="number"
                           step="0.01"
                           {...form.register("avgTicket", { valueAsNumber: true })}
                        />
                     </FormRow>

                     <SliderRow
                        label="% of orders with errors"
                        description="Incorrect items, remakes, voids/re-rings (estimate)."
                        value={errorRate}
                        min={1}
                        max={10}
                        step={0.5}
                        rangeText="Range: 1% – 10%"
                        onChange={(v) => form.setValue("errorRate", v)}
                     />

                     <SliderRow
                        label="Estimated lost orders during peak (%)"
                        description="Checkout slowdown that causes walkaways or fewer orders processed."
                        value={peakLossRate}
                        min={0}
                        max={5}
                        step={0.5}
                        rangeText="Range: 0% – 5%"
                        onChange={(v) => form.setValue("peakLossRate", v)}
                     />

                     <SliderRow
                        label="Missed upsell rate (%)"
                        description="Share of orders where an add-on could have happened but didn’t."
                        value={upsellRate}
                        min={0}
                        max={10}
                        step={0.5}
                        rangeText="Range: 0% – 10%"
                        onChange={(v) => form.setValue("upsellRate", v)}
                     />

                     <FormRow
                        label="Average upsell value ($)"
                        description="Default $3 (e.g., drink size upgrade, add-on, side)."
                        error={form.formState.errors.upsellValue?.message}
                     >
                        <Input
                           className="mt-1 w-48 rounded-xl"
                           type="number"
                           step="0.01"
                           {...form.register("upsellValue", { valueAsNumber: true })}
                        />
                     </FormRow>

                     <FormRow
                        label="Number of locations (optional)"
                        description="Please enter at least one location. Add to roll-up across stores."
                        error={form.formState.errors.locations?.message}
                     >
                        <Input
                           className="mt-1 w-48 rounded-xl"
                           type="number"
                           {...form.register("locations", { valueAsNumber: true })}
                        />
                     </FormRow>

                     <div className="flex w-full gap-3 pt-4">
                        <Button type="submit" className="flex-1 bg-[#196EB9] text-white hover:bg-[#155a92]">
                           Calculate
                        </Button>
                        <Button
                           type="button"
                           onClick={reset}
                           className="flex-1 bg-[#EB5A23] text-white hover:bg-[#c1481b]"
                        >
                           Reset
                        </Button>
                     </div>
                  </form>
                  <p className="pt-5 text-xs text-muted-foreground">
                     Note: This calculator is designed to be conservative and directional. Use it to identify where
                     front-line revenue leakage may be occurring.
                  </p>
               </CardContent>
            </Card>

            {/* RESULTS CARD */}
            <Card className="rounded-2xl shadow-lg">
               <CardHeader>
                  <CardTitle className="text-center text-lg font-semibold">Results</CardTitle>
                  <div className="h-[2px] w-full rounded-full bg-muted" />
               </CardHeader>

               <CardContent className="space-y-6">
                  {!result && <p className="text-muted-foreground">Please enter inputs and click calculate.</p>}

                  {result && (
                     <>
                        <div className="space-y-1 rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-100 p-4 shadow-sm">
                           <p className="text-center text-xs text-muted-foreground">Estimated leakage per month</p>
                           <p className="text-center text-2xl font-black">{fmtCurrency(result.monthly)}</p>
                           <p className="text-xs text-muted-foreground">
                              Based on your inputs, this location may be losing{" "}
                              <strong>{fmtCurrency(result.monthly)}</strong> per month due to order errors, peak-hour
                              slowdown, and missed upsells.
                           </p>
                        </div>

                        <div className="grid gap-4 lg:grid-cols-2">
                           <Metric label="Annualized leakage" value={`${fmtCurrency(result.annual)}/yr`} />
                           <Metric label="Across all locations" value={`${fmtCurrency(result.rollup)}/mo`} />
                        </div>

                        <div className="space-y-2">
                           <Bar
                              label={`Errors ${fmtCurrency(result.errLoss)}/mo`}
                              width={errWidth}
                              color="bg-red-500"
                           />
                           <Bar
                              label={`Peak-hour losses ${fmtCurrency(result.peakLoss)}/mo`}
                              width={peakWidth}
                              color="bg-yellow-500"
                           />
                           <Bar
                              label={`Upsell missed ${fmtCurrency(result.upsellLoss)}/mo`}
                              width={upsellWidth}
                              color="bg-green-500"
                           />
                        </div>

                        <PromoCard
                           title="Ready to reduce front-line leakage?"
                           description="See how POS improves order flow, reduces errors, and increases throughput during peak."
                           buttonText="Check POS Revenue"
                           onClick={() => (window.location.href = "https://plumpos.com/")}
                        />
                     </>
                  )}
                  <p className="text-xs text-muted-foreground">
                     Note: Results vary by concept, menu complexity, staffing, and peak-hour demand. This tool provides
                     an estimate based on the inputs you provided.
                  </p>
               </CardContent>
            </Card>
         </div>
      </div>
   )
}
